import axios, { AxiosError, AxiosPromise } from 'axios';
import { useQuery, UseQueryResult } from 'react-query';

import routes from 'routes';
import {
  CACHE_STALE_TIME,
  MonitoringQueryKey,
  REFETCH_INTERVAL,
  FETCH_TIMEOUT_TIME,
} from 'modules/system/utils/prefetchQueries';
import { SystemType } from 'api/types';
import { DateRange } from './utils/DateRange';
import { getRequestParams } from './utils/getRequestParams';
import {
  BucketSize,
  DateRangeZoomLevel,
  EnergyUnit,
  HomeConsumptionBreakdownResponse,
  PowerFlowDataResponse,
  PowerUnit,
  SiteTimezoneResponse,
} from './types';
import {
  EnergyAllocationType,
  ProductionSummary,
  QualitySummary,
  RawMonitoringDatumType,
  SystemCategory,
  VppDetails,
} from './monitoringTypes';

export type GetMonitoringRequestParams = {
  systemId: string;
  dateRange?: DateRange | null;
};

type ErrorResponse = {
  detail?: string | string[];
  default_code?: string;
  error_codes?: string;
};

export function patchSystem(id: string, body: {}) {
  return axios.patch(routes.system(id), body);
}

export function backfillWorkflows(workflow_ids: string[]) {
  return axios.post(routes.backfillWorkflows, { workflow_ids });
}

export function getSiteTimezone(systemId: string): AxiosPromise<SiteTimezoneResponse> {
  return axios.get(routes.siteTimezone(systemId));
}

export function getSolarPower({
  systemId,
  dateRange,
}: GetMonitoringRequestParams): AxiosPromise<RawMonitoringDatumType<PowerUnit>[]> {
  return axios.get(routes.solarPower(systemId), {
    params: getRequestParams(dateRange),
  });
}

export function getSolarEnergy({
  systemId,
  dateRange,
}: GetMonitoringRequestParams): AxiosPromise<RawMonitoringDatumType<EnergyUnit>[]> {
  return axios.get(routes.solarEnergy(systemId), {
    params: getRequestParams(dateRange),
  });
}

export function getSolarEnergyWithEnergyAllocation({
  systemId,
  dateRange,
}: GetMonitoringRequestParams): AxiosPromise<{
  monitoring_data: RawMonitoringDatumType<EnergyUnit>[];
  energy_allocation: EnergyAllocationType;
  production_summary: ProductionSummary;
}> {
  return axios.get(routes.solarEnergy(systemId), {
    params: { ...getRequestParams(dateRange), include_energy_allocation: 'true' },
  });
}

export type GetSoloarEnergyWithEnergyAllocationResult = {
  monitoring_data: RawMonitoringDatumType<EnergyUnit>[];
  energy_allocation: EnergyAllocationType;
  production_summary: ProductionSummary;
};

export const useGetSolarEnergyWithEnergyAllocation = ({
  systemId,
  dateRange,
}: {
  systemId?: string;
  dateRange?: DateRange;
}): UseQueryResult<GetSoloarEnergyWithEnergyAllocationResult, AxiosError> =>
  useQuery(
    [MonitoringQueryKey.ENERGY_TODAY_WITH_ENERGY_ALLOCATION, systemId, dateRange],
    () =>
      axios
        .get(routes.solarEnergy(systemId!), {
          params: { ...getRequestParams(dateRange), include_energy_allocation: true },
          timeout: FETCH_TIMEOUT_TIME,
        })
        .then((res) => res.data),
    {
      enabled: !!systemId && !!dateRange,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: CACHE_STALE_TIME,
    },
  );

export const useGetSiteTimezone = (
  system?: SystemType,
): UseQueryResult<SiteTimezoneResponse, AxiosError> =>
  useQuery(
    [MonitoringQueryKey.TIMEZONE, system],
    () => axios.get(routes.siteTimezone(system?.id!)).then((res) => res.data),
    {
      enabled: Boolean(system?.id && !system.site_timezone),
      refetchInterval: REFETCH_INTERVAL,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: CACHE_STALE_TIME,
    },
  );

export type GetPowerWithAllocationResult = {
  monitoring_data: RawMonitoringDatumType<PowerUnit>[];
  energy_allocation: EnergyAllocationType;
  production_summary: ProductionSummary;
  quality_summary?: QualitySummary;
};

export const useGetPowerWithAllocation = ({
  systemId,
  category,
  dateRange,
}: {
  systemId?: string;
  category: SystemCategory;
  dateRange?: DateRange;
}): UseQueryResult<GetPowerWithAllocationResult, AxiosError<ErrorResponse>> =>
  useQuery(
    [MonitoringQueryKey.POWER_WITH_ALLOCATION, systemId, category, dateRange],
    () =>
      axios
        .get(routes.powerWithAllocation(systemId!, category), {
          params: { ...getRequestParams(dateRange), include_energy_allocation: true },
          timeout: FETCH_TIMEOUT_TIME,
        })
        .then((res) => res.data),
    {
      enabled: !!systemId && dateRange && dateRange.zoomLevel === DateRangeZoomLevel.DAY,
      refetchInterval: REFETCH_INTERVAL,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: CACHE_STALE_TIME,
    },
  );

export type GetEnergyWithAllocationResult = {
  monitoring_data: RawMonitoringDatumType<EnergyUnit>[];
  energy_allocation: EnergyAllocationType;
  production_summary: ProductionSummary;
  quality_summary?: QualitySummary;
};

export const useGetEnergyWithAllocation = ({
  systemId,
  category,
  dateRange,
}: {
  systemId: string;
  category: SystemCategory;
  dateRange: DateRange;
}): UseQueryResult<GetEnergyWithAllocationResult, AxiosError> =>
  useQuery(
    [MonitoringQueryKey.ENERGY_TODAY_WITH_ENERGY_ALLOCATION, systemId, category, dateRange],
    () =>
      axios
        .get(routes.energyWithAllocation(systemId, category), {
          params: { ...getRequestParams(dateRange), include_energy_allocation: true },
          timeout: FETCH_TIMEOUT_TIME,
        })
        .then((res) => res.data),
    {
      enabled: !!systemId && dateRange && dateRange.zoomLevel !== DateRangeZoomLevel.DAY,
      refetchInterval: REFETCH_INTERVAL,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: CACHE_STALE_TIME,
    },
  );

export function getVppDetails({ systemId }: GetMonitoringRequestParams): AxiosPromise<VppDetails> {
  return axios.get(routes.vppDetails(systemId), {});
}

export const useGetVppDetails = ({
  systemId,
}: GetMonitoringRequestParams): UseQueryResult<VppDetails, AxiosError> =>
  useQuery(
    [MonitoringQueryKey.VPP_DETAILS, systemId],
    () => getVppDetails({ systemId }).then((res) => res.data),
    {
      enabled: !!systemId,
      refetchInterval: REFETCH_INTERVAL,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: CACHE_STALE_TIME,
    },
  );

export const useGetPowerFlow = (
  systemId?: string,
  refetchInterval?: number,
): UseQueryResult<PowerFlowDataResponse, AxiosError> =>
  useQuery(
    [MonitoringQueryKey.POWER_FLOW],
    () => axios.get(routes.powerFlow(systemId!)).then((res) => res.data),
    {
      enabled: !!systemId,
      refetchInterval,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: CACHE_STALE_TIME,
    },
  );

type GetHomeConsumptionBreakdownParams = {
  systemId?: string;
  dateRange: DateRange | null;
  aggregation_level: BucketSize;
};

export const useGetHomeConsumptionBreakdown = ({
  systemId,
  dateRange,
  aggregation_level,
}: GetHomeConsumptionBreakdownParams): UseQueryResult<
  HomeConsumptionBreakdownResponse,
  AxiosError
> =>
  useQuery(
    [MonitoringQueryKey.CONSUMPTION_BREAKDOWN],
    () =>
      axios
        .get(routes.homeEnergyBreakdown(systemId!), {
          params: { ...getRequestParams(dateRange), aggregation_level },
        })
        .then((res) => res.data),
    {
      enabled: !!systemId && !!dateRange,
      refetchInterval: REFETCH_INTERVAL,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: CACHE_STALE_TIME,
    },
  );
